<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('lendable-book.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'lendable-books' }">{{ $t('lendable-book.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <b-form @submit="submitKeyword">
          <b-input-group size="lg">
            <b-form-input
              style="border-color: black; color: black"
              :placeholder="$t('search.keyword-inputor.hint')"
              v-model="keyword"
            ></b-form-input>
            <b-input-group-append>
              <b-button type="submit" variant="primary">{{ $t('search.button.search.label') }}</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form>
      </div>
    </div>
    <b-overlay :show="is_loading" rounded="sm" opacity="0.6" no-center>
      <lendable-book-card-list
        class="mt-3"
        :books="results"
        :show-search-bar="false"
        v-if="search_completed && results.length > 0"
      ></lendable-book-card-list>
      <div class="row" v-if="search_completed && results.length > 0">
        <div class="col-12">
          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="per_page"
            @change="pageChange"
            align="center"
            size="lg"
          ></b-pagination>
        </div>
      </div>
      <div class="row mt-3" v-if="search_completed && results.length === 0">
        <div class="col-12">
          <h5 class="text-danger font-weight-bold">{{ $t('search.message.no-result') }}</h5>
        </div>
      </div>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="arrow-clockwise" font-scale="5" animation="spin-pulse"></b-icon>
        </div>
      </template>
    </b-overlay>
    <b-modal
      id="lendable-book-notice-modal"
      :title="$t('lendable-book.list.modal.library-collection-inquiry-notice.title')"
      ok-only
      centered
    >
      <p class="mb-2 font-weight-bold text-primary">{{ $t('lendable-book.list.modal.library-collection-inquiry-notice.message.cannot-provide-borrow-and-return-services') }}</p>
      <p class="mb-2 font-weight-bold text-primary">{{ $t('lendable-book.list.modal.library-collection-inquiry-notice.message.borrow-in-person') }}</p>
      <p class="mb-0 font-weight-bold text-danger">{{ $t('lendable-book.list.modal.library-collection-inquiry-notice.message.includes-books-that-are-borrowed-but-not-returned-and-lost') }}</p>
      <template #modal-footer="{ ok }">
        <b-button class="w-100" variant="primary" @click="ok()">{{ $t('modal.button.close.label') }}</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'

import { BreadcrumbPlugin } from 'bootstrap-vue'
import { ButtonPlugin } from 'bootstrap-vue'
import { FormInputPlugin } from 'bootstrap-vue'
import { FormPlugin } from 'bootstrap-vue'
import { IconsPlugin } from 'bootstrap-vue'
import { InputGroupPlugin } from 'bootstrap-vue'
import { ModalPlugin } from 'bootstrap-vue'
import { OverlayPlugin } from 'bootstrap-vue'
import { PaginationPlugin } from 'bootstrap-vue'

import LendableBookCardList from '@/components/lists/LendableBookCardList'

import i18n from '@/plugins/i18n.js'

import LendableBookApiService from '@/services/budaedu-api-services/lendable-book-api-service.js'

Vue.use(BreadcrumbPlugin)
Vue.use(ButtonPlugin)
Vue.use(FormInputPlugin)
Vue.use(FormPlugin)
Vue.use(IconsPlugin)
Vue.use(InputGroupPlugin)
Vue.use(ModalPlugin)
Vue.use(OverlayPlugin)
Vue.use(PaginationPlugin)

Vue.use(LendableBookApiService)

export default {
  i18n,
  name: 'lendable-books',
  data() {
    return {
      is_loading: false,
      keyword: '',
      search_completed: false,
      per_page: 30,
      page: 1,
      total: 0,
      results: []
    }
  },
  computed: {
    title() {
      return this.$t('search.type-selector.option.lendable_book.label')
    },
    /**
     * 將使用者輸入的關鍵字（以空格隔開）解析為陣列
     * 同時支援半形空格、全形空格，並自動 trim 與合併連續空白
     */
    parsedKeywords() {
      return this.keyword.trim().split(/[\s\u3000]+/).filter(k => k.length > 0)
    }
  },
  methods: {
    submitKeyword(event) {
      event.preventDefault()
      this.page = 1
      this.setResultsAndPaging()
    },
    pageChange(page) {
      this.page = page
      this.setResultsAndPaging()
    },
    async setResultsAndPaging() {
      this.search_completed = false
      this.is_loading = true
      this.results = []
      try {
        const items = await this.getLendableBooks()

        this.results = items.data
        this.total = items.meta.total
      } finally {
        this.is_loading = false
        this.search_completed = true
      }
    },
    async getLendableBooks() {
      const additional = `order=added_date,desc&per_page=${this.per_page}&page=${this.page}`

      if (this.parsedKeywords.length > 1) {
        // 多關鍵字：以逗號分隔傳入 filter[of_keywords]（AND 邏輯）
        return await this.$lendableBookApiService.queryByKeywords(
          this.parsedKeywords.join(','),
          additional
        )
      }

      if (this.parsedKeywords.length === 1) {
        // 單一關鍵字：維持原有 filter[of_keyword]
        return await this.$lendableBookApiService.queryByKeyword(
          this.parsedKeywords[0],
          additional
        )
      }

      return await this.$lendableBookApiService.query(additional)
    }
  },
  async created() {
    await this.setResultsAndPaging()
  },
  mounted() {
    this.$bvModal.show('lendable-book-notice-modal')
  },
  components: {
    LendableBookCardList
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
}
</script>
